import { CreateEventTicketDto, EventPriceDto } from '@sweatpals/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isNumber } from 'lodash-es';

export const useTickets = ({ initialData, prices = [] }: {
	initialData?: CreateEventTicketDto[];
	prices?: EventPriceDto[];
}): {
	tickets: CreateEventTicketDto[];
	ticketsQuantity: number;
	setTickets(value: (((prevState: CreateEventTicketDto[]) => CreateEventTicketDto[]) | CreateEventTicketDto[])): void;
	addTicket(tierId: string): void;
	removeTicket(tierId: string): void;
	clearTickets(): void;
} => {
	const [tickets, setTickets] = useState<CreateEventTicketDto[]>([]);

	useEffect(() => {
		if (initialData) {
			setTickets(initialData);
		}
	}, [initialData]);

	const ticketsQuantity = useMemo(() => tickets.reduce((acc, t) => acc + t.quantity, 0), [tickets]);

	const addTicket = useCallback((tierId: string): void => {
		const foundTier = prices.find(eventPrice => eventPrice.id === tierId);

		if (!foundTier) {
			return;
		}

		setTickets(prevTickets => {
			const newTickets = [...prevTickets];
			const { maxTickets, maxTicketsPerOrder } = foundTier;
			const ticketsCount = foundTier?.ticketsCount ?? 0;
			const minTicketsPerOrder = foundTier?.minTicketsPerOrder ?? 1;

			const ticketIndex = prevTickets.findIndex(ticket => ticket.eventPriceId === tierId);
			const foundTicket = prevTickets.find(ticket => ticket.eventPriceId === tierId);
			const foundTicketQuantity = foundTicket?.quantity ?? 0;

			if (
				(isNumber(maxTickets) && (ticketsCount + foundTicketQuantity) >= maxTickets)
				|| (isNumber(maxTicketsPerOrder) && foundTicketQuantity >= maxTicketsPerOrder)
			) {
				return prevTickets;
			}

			if (ticketIndex === -1) {
				newTickets.push({
					eventPriceId: tierId,
					quantity: minTicketsPerOrder,
				});
			} else {
				newTickets[ticketIndex].quantity += 1;
			}

			return newTickets;
		});
	}, [prices]);

	const removeTicket = useCallback((tierId: string): void => {
		const foundTier = prices.find(eventPrice => eventPrice.id === tierId);
		const ticketIndex = tickets.findIndex(ticket => ticket.eventPriceId === tierId);

		if (!foundTier || ticketIndex < 0) {
			return;
		}

		setTickets(prevTickets => {
			const newTickets = [...prevTickets];
			const minTicketsPerOrder = foundTier.minTicketsPerOrder ?? 1;

			if (ticketIndex !== -1) {
				if (newTickets[ticketIndex].quantity === minTicketsPerOrder) {
					newTickets.splice(ticketIndex, 1);
				} else {
					newTickets[ticketIndex].quantity -= 1;
				}
			}

			return newTickets;
		});
	}, [prices, tickets]);

	const clearTickets = useCallback((): void => {
		setTickets([]);
	}, []);

	return {
		tickets,
		ticketsQuantity,
		setTickets,
		addTicket,
		removeTicket,
		clearTickets,
	};
};
